/**
 * PWA lifecycle helpers for StUpEco Hub.
 *
 * - registerServiceWorker(): registers /service-worker.js in production only.
 *   Uses a one-time "controllerchange" reload so users pick up new deploys.
 * - installPromptStore: tiny event-driven store around beforeinstallprompt
 *   so any component can render a subtle "Install" CTA.
 */

const SW_URL = "/service-worker.js";

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  const state = getInstallState();
  listeners.forEach((cb) => {
    try { cb(state); } catch { /* ignore */ }
  });
}

function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

export function getInstallState() {
  return {
    canInstall: !!deferredPrompt,
    isStandalone: isStandalone(),
  };
}

export function subscribeInstall(cb) {
  listeners.add(cb);
  cb(getInstallState());
  return () => listeners.delete(cb);
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  const evt = deferredPrompt;
  deferredPrompt = null;
  notify();
  evt.prompt();
  try {
    const choice = await evt.userChoice;
    return choice; // { outcome: "accepted"|"dismissed" }
  } catch {
    return { outcome: "dismissed" };
  }
}

// beforeinstallprompt (Chromium/Edge/Android)
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferredPrompt = e;
  notify();
});

// Once installed, hide the CTA forever
window.addEventListener("appinstalled", () => {
  deferredPrompt = null;
  notify();
});

// Standalone state can change (e.g. iOS PWA), so re-notify on visibility
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") notify();
});

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  // Only register in production builds. CRA dev has HMR + differing asset URLs.
  if (process.env.NODE_ENV !== "production") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: "/" })
      .then((reg) => {
        // Detect updates and roll to the new SW automatically.
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              // A new SW has taken control-in-waiting. Ask it to activate.
              try { nw.postMessage({ type: "SKIP_WAITING" }); } catch { /* ignore */ }
            }
          });
        });
        // Poll for updates every 30 minutes while the tab is open
        setInterval(() => { reg.update().catch(() => {}); }, 30 * 60 * 1000);
      })
      .catch(() => { /* registration is best-effort */ });

    // Reload once when a new SW takes control
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}
